/**
 * BlockBasedMenuRenderer Component
 * 
 * Renders a published menu layout for customers.
 * Fetches the published layout and renders blocks in preview mode.
 * All operational data (prices, availability) is fetched fresh from the database.
 */

import React, { memo, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { usePublishedMenuLayout } from '@/hooks/useMenuBuilderLayout';
import { BlockRenderer } from '../blocks/BlockRenderer';
import { Skeleton } from '@/components/ui/skeleton';
import { AnyMenuBlock } from '@/types/menuBuilder';
import { useGlobalThemeStyles } from '@/hooks/useGlobalThemeStyles';
import { Branch } from '@/types/index';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';

interface BlockBasedMenuRendererProps {
  branchId: string;
  className?: string;
  branch?: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  preloadedData?: PublicMenuData;
}

export const BlockBasedMenuRenderer = memo(function BlockBasedMenuRenderer({
  branchId,
  className,
  branch,
  enableRatings = false,
  enableOrdering = false,
  preloadedData,
}: BlockBasedMenuRendererProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Only fetch layout if NOT using preloaded data
  const { layout, isLoading, error } = usePublishedMenuLayout(
    preloadedData?.layout ? '' : branchId // Disable query when preloaded
  );
  
  const effectiveLayout = preloadedData?.layout || layout;
  const backgroundStyle = useGlobalThemeStyles(effectiveLayout?.globalThemeOverrides);

  // Get sorted blocks
  const blocks = useMemo(() => {
    if (!effectiveLayout?.blocks) return [];
    return [...effectiveLayout.blocks].sort((a: any, b: any) => a.order - b.order);
  }, [effectiveLayout]);

  // Handle search from SearchBlock
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Filter category blocks based on search query
  // Note: The actual filtering happens within CategoryBlock based on item names
  // This is just passed down for context

  if (isLoading && !preloadedData) {
    return (
      <div className={cn('space-y-4 p-4', className)}>
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if ((error && !preloadedData) || !effectiveLayout) {
    return (
      <div className={cn('p-8 text-center', className)}>
        <p className="text-muted-foreground">
          {t('menu.loadError', 'Unable to load menu. Please try again later.')}
        </p>
      </div>
    );
  }

  if (blocks.length === 0) {
    return (
      <div className={cn('p-8 text-center', className)}>
        <p className="text-muted-foreground">
          {t('menu.noContent', 'Menu content is being prepared.')}
        </p>
      </div>
    );
  }

  return (
    <div 
      className={cn('space-y-0 min-h-screen', className)}
      style={backgroundStyle}
    >
      {blocks.map((block) => (
        <BlockRenderer
          key={block.id}
          block={block}
          isPreview={true}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          branch={branch}
          enableRatings={enableRatings}
          enableOrdering={enableOrdering}
          preloadedData={preloadedData}
        />
      ))}
    </div>
  );
});

export default BlockBasedMenuRenderer;
